import React, { useState } from 'react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { formatGradeOneDecimal, floorToOneDecimal } from '../../utils/gradeCalculation';
import logoWide from '../../assets/logo_wide.png';
import './CohortGradeMatrix.css';

const PURPLE = '7030A0';
const BLUE = '0070C0';
const YELLOW = 'FFFF00';
const RED = 'FF0000';
const STUDENTS_PER_PDF_PAGE = 7;

// Schooljaar loopt van september t/m augustus.
export const defaultSchoolYear = (date = new Date()) => {
  const year = date.getFullYear();
  return date.getMonth() >= 8 ? `${year}-${year + 1}` : `${year - 1}-${year}`;
};

const gradeText = (value) => (typeof value === 'number' ? formatGradeOneDecimal(value) : '');
const isFail = (value, passScore = 5.5) => typeof value === 'number' && floorToOneDecimal(value) < passScore;

const fetchLogoBase64 = async () => {
  try {
    const response = await fetch(logoWide);
    const buffer = await response.arrayBuffer();
    let binary = '';
    new Uint8Array(buffer).forEach((byte) => { binary += String.fromCharCode(byte); });
    return window.btoa(binary);
  } catch (error) {
    return null;
  }
};

/**
 * Cijferoverzicht per cohort: studenten als kolommen, per vak een regel met het eindcijfer
 * en daaronder de examenpunten met weging. Ontbrekend cijfer = geel, onvoldoende = rood.
 *
 * matrix = { students: [{ id, name, code }], courses: [{ code, name, ects, points: [{ label, passScore }],
 *            cells: [{ finalGrade, pointGrades: [number|null] }] }], cohortName, studyName, yearName }
 */
const CohortGradeMatrix = ({ matrix }) => {
  const [schoolYear, setSchoolYear] = useState(defaultSchoolYear());
  const [exporting, setExporting] = useState('');

  if (!matrix) return null;
  const { students, courses } = matrix;
  const fileBase = `Cijferoverzicht_${String(matrix.cohortName || 'cohort').replace(/[^\w-]+/g, '_')}_${String(matrix.yearName || '').replace(/[^\w-]+/g, '_')}`;

  const exportExcel = async () => {
    setExporting('excel');
    try {
      const { default: ExcelJS } = await import('exceljs');
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet((matrix.cohortName || 'Cijferoverzicht').slice(0, 31), {
        views: [{ state: 'frozen', xSplit: 1, ySplit: 2 }],
        pageSetup: { orientation: 'landscape', paperSize: 9, fitToPage: true, fitToWidth: 1, fitToHeight: 0 }
      });

      sheet.columns = [{ width: 34 }, ...students.map(() => ({ width: 17 }))];
      const thin = { style: 'thin', color: { argb: 'FF000000' } };
      const border = { top: thin, left: thin, bottom: thin, right: thin };
      const fill = (hex) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${hex}` } });

      const headerNames = sheet.getRow(1);
      const headerCodes = sheet.getRow(2);
      headerNames.height = 34;
      headerCodes.getCell(1).value = schoolYear;
      headerCodes.getCell(1).alignment = { horizontal: 'center' };
      headerCodes.getCell(1).border = border;
      students.forEach((student, index) => {
        const nameCell = headerNames.getCell(index + 2);
        nameCell.value = student.name;
        nameCell.font = { bold: true, color: { argb: 'FFFFFFFF' }, underline: true };
        nameCell.fill = fill(PURPLE);
        nameCell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        nameCell.border = border;
        const codeCell = headerCodes.getCell(index + 2);
        codeCell.value = student.code || '';
        codeCell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        codeCell.fill = fill(PURPLE);
        codeCell.alignment = { horizontal: 'center' };
        codeCell.border = border;
      });

      const logo = await fetchLogoBase64();
      if (logo) {
        const imageId = workbook.addImage({ base64: logo, extension: 'png' });
        sheet.addImage(imageId, { tl: { col: 0.1, row: 0.15 }, ext: { width: 200, height: 40 } });
      }

      let rowIndex = 3;
      courses.forEach((course) => {
        const courseRow = sheet.getRow(rowIndex);
        const label = courseRow.getCell(1);
        label.value = `${course.name}${course.ects ? ` (${course.ects} ECTS)` : ''}`;
        label.font = { bold: true, color: { argb: `FF${BLUE}` }, underline: true };
        label.border = border;
        course.cells.forEach((cell, index) => {
          const target = courseRow.getCell(index + 2);
          target.value = typeof cell.finalGrade === 'number' ? floorToOneDecimal(cell.finalGrade) : null;
          target.numFmt = '0.0';
          target.font = { bold: true, color: { argb: 'FFFFFFFF' } };
          target.fill = fill(BLUE);
          target.alignment = { horizontal: 'center' };
          target.border = border;
        });
        rowIndex += 1;

        course.points.forEach((point, pointIndex) => {
          const pointRow = sheet.getRow(rowIndex);
          pointRow.getCell(1).value = point.label;
          pointRow.getCell(1).border = border;
          course.cells.forEach((cell, index) => {
            const grade = cell.pointGrades[pointIndex];
            const target = pointRow.getCell(index + 2);
            target.value = typeof grade === 'number' ? floorToOneDecimal(grade) : null;
            target.numFmt = '0.0';
            target.alignment = { horizontal: 'center' };
            target.border = border;
            if (typeof grade !== 'number') target.fill = fill(YELLOW);
            else if (isFail(grade, point.passScore)) target.font = { color: { argb: `FF${RED}` } };
          });
          rowIndex += 1;
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${fileBase}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      window.alert(error.message || 'Excel-export mislukt');
    } finally {
      setExporting('');
    }
  };

  // PDF: studenten in blokken van STUDENTS_PER_PDF_PAGE naast elkaar, vakken en examenpunten herhaald per blok.
  const exportPDF = async () => {
    setExporting('pdf');
    try {
      const doc = new jsPDF({ orientation: 'landscape' });
      const logo = await fetchLogoBase64();
      const chunks = [];
      for (let start = 0; start < students.length; start += STUDENTS_PER_PDF_PAGE) {
        chunks.push({ start, students: students.slice(start, start + STUDENTS_PER_PDF_PAGE) });
      }
      if (!chunks.length) chunks.push({ start: 0, students: [] });

      chunks.forEach((chunk, chunkIndex) => {
        if (chunkIndex > 0) doc.addPage();
        if (logo) doc.addImage(`data:image/png;base64,${logo}`, 'PNG', 14, 8, 62, 14);
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        doc.text(`Cijferoverzicht ${matrix.cohortName || ''}`, 82, 14);
        doc.setFontSize(9);
        doc.setTextColor(90, 90, 90);
        doc.text(
          `${matrix.studyName || ''} · ${matrix.yearName || ''} · Schooljaar ${schoolYear} · Studenten ${chunk.start + 1}-${chunk.start + chunk.students.length} van ${students.length}`,
          82,
          20
        );

        const meta = [];
        const body = [];
        courses.forEach((course) => {
          meta.push({ type: 'course' });
          body.push([
            `${course.name}${course.ects ? ` (${course.ects} ECTS)` : ''}`,
            ...chunk.students.map((_, i) => gradeText(course.cells[chunk.start + i].finalGrade))
          ]);
          course.points.forEach((point, pointIndex) => {
            meta.push({ type: 'point', pointIndex, course, passScore: point.passScore });
            body.push([
              point.label,
              ...chunk.students.map((_, i) => gradeText(course.cells[chunk.start + i].pointGrades[pointIndex]))
            ]);
          });
        });

        doc.autoTable({
          startY: 26,
          head: [
            ['', ...chunk.students.map((student) => student.name)],
            [schoolYear, ...chunk.students.map((student) => student.code || '')]
          ],
          body,
          theme: 'grid',
          styles: { fontSize: 7.5, cellPadding: 1.4, halign: 'center', lineColor: [0, 0, 0], lineWidth: 0.1 },
          headStyles: { fillColor: [112, 48, 160], textColor: [255, 255, 255], fontStyle: 'bold' },
          columnStyles: { 0: { halign: 'left', cellWidth: 62 } },
          didParseCell: (hook) => {
            if (hook.section !== 'body') return;
            const rowMeta = meta[hook.row.index];
            if (rowMeta.type === 'course') {
              hook.cell.styles.fontStyle = 'bold';
              if (hook.column.index === 0) {
                hook.cell.styles.textColor = [0, 112, 192];
              } else {
                hook.cell.styles.fillColor = [0, 112, 192];
                hook.cell.styles.textColor = [255, 255, 255];
              }
              return;
            }
            if (hook.column.index === 0) return;
            const cell = rowMeta.course.cells[chunk.start + hook.column.index - 1];
            const grade = cell.pointGrades[rowMeta.pointIndex];
            if (typeof grade !== 'number') hook.cell.styles.fillColor = [255, 255, 0];
            else if (isFail(grade, rowMeta.passScore)) hook.cell.styles.textColor = [255, 0, 0];
          }
        });
      });

      const pageCount = doc.internal.getNumberOfPages();
      for (let page = 1; page <= pageCount; page += 1) {
        doc.setPage(page);
        doc.setFontSize(7);
        doc.setTextColor(120, 120, 120);
        doc.text(`Pagina ${page} van ${pageCount}`, doc.internal.pageSize.getWidth() - 30, doc.internal.pageSize.getHeight() - 6);
      }
      doc.save(`${fileBase}.pdf`);
    } catch (error) {
      window.alert(error.message || 'PDF-export mislukt');
    } finally {
      setExporting('');
    }
  };

  return (
    <div className="report-preview cgm">
      <div className="cgm-toolbar">
        <div>
          <h3>Cijferoverzicht {matrix.cohortName}</h3>
          <p className="cgm-muted">{matrix.studyName} · {matrix.yearName} · {students.length} studenten · geel = nog geen cijfer, rood = onvoldoende</p>
        </div>
        <div className="cgm-actions">
          <label>
            Schooljaar
            <input value={schoolYear} onChange={(e) => setSchoolYear(e.target.value)} />
          </label>
          <button type="button" className="btn-secondary" onClick={exportPDF} disabled={!!exporting || !students.length}>
            {exporting === 'pdf' ? 'PDF maken…' : 'PDF'}
          </button>
          <button type="button" className="btn-primary" onClick={exportExcel} disabled={!!exporting || !students.length}>
            {exporting === 'excel' ? 'Excel maken…' : 'Excel'}
          </button>
        </div>
      </div>

      {students.length === 0 ? (
        <p>Geen studenten in dit cohort met inschrijvingen of cijfers in dit studiejaar.</p>
      ) : (
        <div className="cgm-wrap">
          <table className="cgm-table">
            <thead>
              <tr>
                <th className="cgm-logo-cell" rowSpan={1}><img src={logoWide} alt="NLBA" /></th>
                {students.map((student) => <th key={student.id} className="cgm-name">{student.name}</th>)}
              </tr>
              <tr>
                <th className="cgm-year">{schoolYear}</th>
                {students.map((student) => <th key={student.id} className="cgm-code">{student.code || '-'}</th>)}
              </tr>
            </thead>
            <tbody>
              {courses.map((course) => (
                <React.Fragment key={course.code || course.name}>
                  <tr className="cgm-course-row">
                    <td className="cgm-label">{course.name}{course.ects ? <span className="cgm-ects"> ({course.ects} ECTS)</span> : null}</td>
                    {course.cells.map((cell, index) => (
                      <td key={students[index].id}>{gradeText(cell.finalGrade)}</td>
                    ))}
                  </tr>
                  {course.points.map((point, pointIndex) => (
                    <tr key={`${course.code}-${pointIndex}`} className="cgm-point-row">
                      <td className="cgm-label">{point.label}</td>
                      {course.cells.map((cell, index) => {
                        const grade = cell.pointGrades[pointIndex];
                        const className = typeof grade !== 'number' ? 'cgm-missing' : (isFail(grade, point.passScore) ? 'cgm-fail' : '');
                        return <td key={students[index].id} className={className}>{gradeText(grade)}</td>;
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CohortGradeMatrix;
